import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FullScreenLayout } from '../../routes/layouts/FullScreenLayout';
import { FullScreenHeader } from '@/components/shared/layout';
import { useAuth } from '../../contexts/AuthContext';
import { useClassDetail } from '@/data/queries/useClasses';
import { classDetailDtoToClassItem } from '@/utils/classUtils';
import { usePracticeStatus } from '@/data/queries/usePracticeStatus';
import { useClassEvaluation } from '@/data/queries/useEvaluation';

type TabType = 'curriculum' | 'evaluation' | 'practice-status' | 'ranking' | 'statistics';

/**
 * 밀리초를 시간 형식으로 변환 (예: 111000ms → "1분 51초")
 */
function formatPlayTime(milliseconds: number | null | undefined): string {
    if (milliseconds == null || milliseconds === 0) return '0초';
    
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;
    
    const parts = [];
    if (hours > 0) parts.push(`${hours}시간`);
    if (minutes > 0) parts.push(`${minutes}분`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}초`);
    
    return parts.join(' ');
}

/**
 * 타임스탬프를 날짜 형식으로 변환 (예: 1721626542000 → "2026. 07. 22 16:45")
 */
function formatPracticeDate(timestamp: number | null | undefined): string {
    if (timestamp == null) return '—';
    
    const date = new Date(timestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}. ${month}. ${day} ${hours}:${minutes}`;
}

export default function PracticeStatus(): React.ReactElement {
    const { classId: classIdParam } = useParams<{ classId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { getCurrentRole } = useAuth();

    const role = getCurrentRole();
    const basePath = role === 'master' ? '/master' : '/admin';
    const classId = parseInt((classIdParam ?? '').trim(), 10);
    const currentTab = searchParams.get('tab') || 'practice-status';

    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');

    const {
        data: classDetailDto,
        isError: isClassError,
        isLoading: isClassLoading,
    } = useClassDetail(classId, {
        enabled: classId > 0,
    });

    const classItem = classDetailDto ? classDetailDtoToClassItem(classDetailDto) : null;

    // 평가결과 API에서 사용자 목록 가져오기
    const {
        data: evaluationData,
        isLoading: isEvaluationLoading,
    } = useClassEvaluation(classId, {}, {
        enabled: classId > 0,
    });

    // 사용자 목록 추출
    const userList = useMemo(() => {
        if (!evaluationData?.userList) return [];
        
        return evaluationData.userList.map(user => ({
            id: user.user_id,
            name: user.user_name,
            email: user.user_email,
        }));
    }, [evaluationData]);

    // 첫 번째 사용자를 기본 선택
    React.useEffect(() => {
        if (userList.length > 0 && selectedUserId === null) {
            setSelectedUserId(userList[0].id);
        }
    }, [userList, selectedUserId]);

    // 선택된 사용자의 플레이 기록 조회
    const {
        data: practiceData,
        isError: isPracticeError,
        isLoading: isPracticeLoading,
    } = usePracticeStatus(classId, selectedUserId || undefined, {
        enabled: classId > 0 && selectedUserId != null,
    });

    // 선택된 사용자
    const selectedUser = useMemo(() => {
        return userList.find(u => u.id === selectedUserId) || null;
    }, [userList, selectedUserId]);

    // 플레이 기록 + 날짜 필터링
    const filteredRecords = useMemo(() => {
        console.log('🔍 [PracticeStatus] practiceData:', practiceData);
        console.log('🔍 [PracticeStatus] ScenaroPlayRecords:', practiceData?.ScenaroPlayRecords);
        
        if (!practiceData?.ScenaroPlayRecords) {
            console.log('❌ [PracticeStatus] ScenaroPlayRecords가 없습니다');
            return [];
        }
        
        console.log('✅ [PracticeStatus] ScenaroPlayRecords 개수:', practiceData.ScenaroPlayRecords.length);
        
        return practiceData.ScenaroPlayRecords.filter((record) => {
            // 날짜 필터링
            if (startDate || endDate) {
                const recordDate = record.started_at;
                if (!recordDate) return false;
                
                const recordDateObj = new Date(recordDate);
                
                if (startDate) {
                    const startDateObj = new Date(startDate);
                    startDateObj.setHours(0, 0, 0, 0);
                    if (recordDateObj < startDateObj) return false;
                }
                
                if (endDate) {
                    const endDateObj = new Date(endDate);
                    endDateObj.setHours(23, 59, 59, 999);
                    if (recordDateObj > endDateObj) return false;
                }
            }
            
            return true;
        });
    }, [practiceData, startDate, endDate]);

    // minPassingScore 가져오기 (클래스 상세에서)
    const minPassingScore = useMemo(() => {
        return classDetailDto?.minPassingScore || 70;
    }, [classDetailDto]);

    // 상태 계산 (완료 여부)
    const getStatus = (score: number | undefined): string => {
        if (score == null) return '';
        return score >= minPassingScore ? '완료' : '';
    };

    const backToClassDetailPath = (): string => {
        return `${basePath}/class-management/${classId}`;
    };

    const handleTabChange = (tab: TabType) => {
        if (tab === 'curriculum') {
            if (classItem?.curriculum?.length) {
                const firstSession = classItem.curriculum[0];
                navigate(`${basePath}/class-management/${classId}/curriculum/${firstSession.id}`);
            }
        } else if (tab === 'evaluation') {
            navigate(`${basePath}/class-management/${classId}/evaluation`);
        } else if (tab === 'practice-status') {
            return;
        } else if (tab === 'statistics') {
            navigate(`${basePath}/class-management/${classId}/statistics`);
        } else if (tab === 'ranking') {
            navigate(`${basePath}/class-management/${classId}/ranking`);
        } else {
            navigate(`${basePath}/class-management/${classId}/session-management?tab=${tab}`);
        }
    };

    const tabs = [
        { id: 'curriculum', label: '커리큘럼' },
        { id: 'evaluation', label: '평가결과' },
        { id: 'practice-status', label: '실습현황' },
        { id: 'ranking', label: '랭킹' },
        { id: 'statistics', label: '실습통계' },
    ];

    const renderCenterContent = () => {
        return (
            <div className="flex gap-2">
                {tabs.map((tab) => (
                    <button
                        key={tab.id}
                        onClick={() => handleTabChange(tab.id as TabType)}
                        className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                            currentTab === tab.id
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                    >
                        {tab.label}
                    </button>
                ))}
            </div>
        );
    };

    const renderRightContent = () => {
        return (
            <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">조회기간</span>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
                <span className="text-gray-600">~</span>
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1 border border-gray-300 rounded text-xs w-32"
                />
            </div>
        );
    };

    if (isClassError || isClassLoading || !classItem) {
        return (
            <FullScreenLayout
                header={
                    <FullScreenHeader
                        title={classItem?.title || '실습현황'}
                        onBack={() => navigate(backToClassDetailPath())}
                        centerContent={renderCenterContent()}
                    />
                }
            >
                <div className="flex items-center justify-center h-full text-gray-500">
                    {isClassLoading ? '로딩 중...' : '데이터를 불러올 수 없습니다'}
                </div>
            </FullScreenLayout>
        );
    }

    const isLoading = isPracticeLoading || isEvaluationLoading;
    const hasError = isPracticeError;

    return (
        <FullScreenLayout
            header={
                <FullScreenHeader
                    title={classItem.title}
                    onBack={() => navigate(backToClassDetailPath())}
                    centerContent={renderCenterContent()}
                    rightContent={renderRightContent()}
                />
            }
        >
            <div className="flex flex-1 min-h-0">
                {/* 좌측 사이드바 */}
                <div className="w-64 bg-gray-50 border-r border-gray-200 flex flex-col">
                    {/* 헤더 (고정) */}
                    <div className="p-4 border-b border-gray-200">
                        <h2 className="text-lg font-bold text-gray-900">
                            실습현황 <span className="text-sm font-normal text-gray-500">(Total {userList.length})</span>
                        </h2>
                    </div>
                    {/* 사용자 리스트 (스크롤) */}
                    <div className="flex-1 overflow-y-auto p-4">
                        {isEvaluationLoading ? (
                            <div className="text-sm text-gray-500 text-center py-4">로딩 중...</div>
                        ) : userList.length === 0 ? (
                            <div className="text-sm text-gray-500 text-center py-4">학습자가 없습니다</div>
                        ) : (
                            <div className="space-y-1">
                                {userList.map((user) => (
                                    <button
                                        key={user.id}
                                        onClick={() => setSelectedUserId(user.id)}
                                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                            selectedUserId === user.id
                                                ? 'bg-blue-100 text-blue-700'
                                                : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    >
                                        <div className="font-medium">{user.name} ({user.email})</div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* 메인 영역 */}
                <div className="flex-1 bg-white overflow-y-auto">
                    <div className="p-8">
                        {/* 사용자 정보 헤더 */}
                        <div className="flex items-center gap-3 mb-6">
                            <h1 className="text-2xl font-bold text-gray-900">{selectedUser?.name}</h1>
                            <span className="text-sm text-gray-500">{selectedUser?.email}</span>
                        </div>

                        {/* 테이블 */}
                        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                                            시나리오
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            실습모드
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            소요시간
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            평가 점수
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            상태
                                        </th>
                                        <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">
                                            실습일시
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {isLoading ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                                                로딩 중...
                                            </td>
                                        </tr>
                                    ) : hasError ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-8 text-center text-sm text-red-500">
                                                데이터를 불러올 수 없습니다
                                            </td>
                                        </tr>
                                    ) : filteredRecords.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">
                                                실습 데이터가 없습니다
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredRecords.map((record, index) => (
                                            <tr
                                                key={index}
                                                className="border-b border-gray-100 hover:bg-gray-50"
                                            >
                                                <td className="px-4 py-3 text-sm text-gray-900">
                                                    {record.content_name || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {record.mode_value_title || record.mode || '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {formatPlayTime(record.play_time)}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {record.score ?? '—'}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-center">
                                                    {getStatus(record.score) ? (
                                                        <span className="text-blue-600 font-medium">
                                                            {getStatus(record.score)}
                                                        </span>
                                                    ) : (
                                                        <span className="text-gray-400">—</span>
                                                    )}
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 text-center">
                                                    {formatPracticeDate(record.started_at)}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        </FullScreenLayout>
    );
}
